"use strict";
var __defProp = Object.defineProperty;
var __getOwnPropDesc = Object.getOwnPropertyDescriptor;
var __decorateClass = (decorators, target, key, kind) => {
  var result = kind > 1 ? void 0 : kind ? __getOwnPropDesc(target, key) : target;
  for (var i = decorators.length - 1, decorator; i >= 0; i--)
    if (decorator = decorators[i])
      result = (kind ? decorator(target, key, result) : decorator(result)) || result;
  if (kind && result) __defProp(target, key, result);
  return result;
};
import { LitElement, html, nothing } from "lit";
import { state, property } from "lit/decorators.js";
import { keyed } from "lit/directives/keyed.js";
import { ref, createRef } from "lit/directives/ref.js";
import { STCComponentMixin } from "../../../common/mixins/stc_component_mixin";
import { titleToSlug } from "./helper_utils";
import { micrositesAiHtmlEnabled, openHtmlImportDialog } from "./microsite_html_import_dialog";
import { openEnhanceAiDialog } from "./microsite_enhance_ai_dialog";
import "./microsite_edit_page_drawer";
function notifyMicrositeUser(payload) {
  const win = window;
  if (win.HIP?.fn?.notify) {
    win.HIP.fn.notify(payload);
    return;
  }
}
export const DUPLICATE_PAGE_TITLE_MESSAGE = "This name is already taken!";
const DELETE_PAGE_DETAILS = "Are you sure you want to delete this page? This action cannot be undone.";
const DELETE_PAGE_IN_CAMPAIGN_DETAILS = "This page is currently used by one or more active campaigns. Deleting this page will invalidate personalized links in those campaigns. Do you want to proceed?";
export function isDuplicatePageTitle(pages, title, excludePageId = null) {
  const normalized = title.trim().toLowerCase();
  if (!normalized) return false;
  return pages.some(
    (p) => p.id !== excludePageId && p.title.trim().toLowerCase() === normalized
  );
}
const TEMPLATE_MANAGE_PREVIEW_EVENT = "microsite:template-manage-preview";
export class MicrositePageManager extends STCComponentMixin(LitElement) {
  constructor() {
    super(...arguments);
    this.pages = [];
    this.editorMode = "visual";
    this.htmlEditorHasContent = false;
    this.currentPageId = null;
    this.previewMode = false;
    this.historyMode = false;
    this.hasPersonalizationOptions = false;
    this.loading = false;
    this.canManageTemplates = false;
    this.siteVersioningEnabled = false;
    this.showAddDialog = false;
    this.showCopyToTemplatesDialog = false;
    this.copyToTemplatesSubmitting = false;
    this.showManageTemplatesDialog = false;
    this.manageTemplatesList = [];
    this.manageTemplatesLoading = false;
    this.manageSelectedTemplateKey = "__current__";
    this.managePreviewPuck = null;
    this.managePreviewLoading = false;
    this.manageDeleteSubmitting = false;
    this.manageLoadSubmitting = false;
    /** Stable handle to the Puck preview mount (hi-dialog may portal; avoid document.getElementById alone). */
    this.manageTemplatePreviewMountRef = createRef();
    this.manageTemplatePreviewSyncAttempts = 0;
    this.openVersionHistory = () => {
      this.dispatchEvent(new CustomEvent("microsite:view-history", { bubbles: true, composed: true }));
    };
  }
  get editDrawer() {
    return this.querySelector("stc-microsite-edit-page-drawer");
  }
  get selectedPage() {
    return this.currentPageId != null ? this.pages.find((p) => p.id === this.currentPageId) ?? null : null;
  }
  emitPageSelect(pageId, titleForNewPage, initialPuckData) {
    this.dispatchEvent(
      new CustomEvent("microsite:page-select", {
        bubbles: true,
        composed: true,
        detail: { pageId, titleForNewPage, initialPuckData }
      })
    );
  }
  emitPagesUpdate(pages) {
    this.dispatchEvent(
      new CustomEvent("microsite:pages-update", {
        bubbles: true,
        composed: true,
        detail: { pages }
      })
    );
  }
  handleDropdownChange(e) {
    const detail = e.detail;
    const target = e.target;
    const val = detail?.value ?? target?.value;
    if (val != null && val !== "") {
      const id = parseInt(val, 10);
      if (!Number.isNaN(id)) {
        this.emitPageSelect(id);
      }
    }
  }
  handleAddPageClick() {
    this.showAddDialog = true;
  }
  handleImportHtmlClick() {
    openHtmlImportDialog();
  }
  handleEnhanceAiClick() {
    openEnhanceAiDialog(this.pages);
  }
  isHtmlEditorMode() {
    return this.editorMode === "html";
  }
  /** In HTML mode, hide page chrome until the user has imported HTML or a ZIP. */
  showHtmlPageManagerChrome() {
    return !this.isHtmlEditorMode() || this.htmlEditorHasContent;
  }
  get canShowHtmlAiChrome() {
    return this.isHtmlEditorMode() && micrositesAiHtmlEnabled() && this.showHtmlPageManagerChrome();
  }
  updated(changedProperties) {
    super.updated?.(changedProperties);
    if (changedProperties.has("showAddDialog") && this.showAddDialog) {
      this.resetAddForm();
    }
    if (changedProperties.has("showCopyToTemplatesDialog") && this.showCopyToTemplatesDialog) {
      requestAnimationFrame(() => {
        const form = this.querySelector("#page-manager-copy-to-templates-form");
        form?.clearValidations?.();
      });
    }
    if (changedProperties.has("showManageTemplatesDialog") && !this.showManageTemplatesDialog) {
      this.manageTemplatePreviewSyncAttempts = 0;
      this.clearTemplateManagePreviewForReact();
    }
    if (changedProperties.has("showManageTemplatesDialog") && this.showManageTemplatesDialog) {
      this.manageSelectedTemplateKey = "__current__";
      void this.openManageTemplatesDialog();
    }
    if (this.showManageTemplatesDialog && (changedProperties.has("managePreviewPuck") || changedProperties.has("managePreviewLoading") || changedProperties.has("showManageTemplatesDialog") && this.showManageTemplatesDialog)) {
      this.scheduleSyncTemplateManagePreviewForReact();
    }
    if (this.showManageTemplatesDialog && !this.manageTemplatesLoading && (changedProperties.has("showManageTemplatesDialog") || changedProperties.has("manageTemplatesLoading") || changedProperties.has("manageTemplatesList") || changedProperties.has("manageSelectedTemplateKey"))) {
      this.scheduleHideManageTemplateComboboxClear();
    }
  }
  dispatchTemplateManagePreview(detail) {
    window.dispatchEvent(new CustomEvent(TEMPLATE_MANAGE_PREVIEW_EVENT, { detail }));
  }
  clearTemplateManagePreviewForReact() {
    this.dispatchTemplateManagePreview({ mount: null, puckData: null });
  }
  scheduleSyncTemplateManagePreviewForReact() {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => this.syncTemplateManagePreviewForReact());
    });
  }
  resolveTemplatePreviewMountEl() {
    const fromRef = this.manageTemplatePreviewMountRef.value;
    if (fromRef?.isConnected) return fromRef;
    const fromHost = this.querySelector("#eab-manage-template-preview-mount");
    if (fromHost instanceof HTMLDivElement && fromHost.isConnected) return fromHost;
    const fromDoc = document.getElementById("eab-manage-template-preview-mount");
    return fromDoc instanceof HTMLDivElement && fromDoc.isConnected ? fromDoc : null;
  }
  syncTemplateManagePreviewForReact() {
    if (!this.showManageTemplatesDialog) return;
    const mount = this.resolveTemplatePreviewMountEl();
    if (!mount) {
      if (this.manageTemplatePreviewSyncAttempts < 25) {
        this.manageTemplatePreviewSyncAttempts += 1;
        window.setTimeout(() => this.syncTemplateManagePreviewForReact(), 50);
      }
      return;
    }
    this.manageTemplatePreviewSyncAttempts = 0;
    if (this.managePreviewLoading) {
      this.dispatchTemplateManagePreview({ mount, puckData: null });
      return;
    }
    this.dispatchTemplateManagePreview({ mount, puckData: this.managePreviewPuck });
  }
  forceHideManageTemplateComboboxClear() {
    if (!this.showManageTemplatesDialog || this.manageTemplatesLoading) return;
    const host = this.querySelector("#microsite-page-manager-manage-template-select");
    const clearEl = host?.shadowRoot?.querySelector("#clear");
    if (clearEl) {
      clearEl.style.setProperty("display", "none", "important");
    }
  }
  scheduleHideManageTemplateComboboxClear() {
    if (!this.showManageTemplatesDialog || this.manageTemplatesLoading) return;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => this.forceHideManageTemplateComboboxClear());
    });
  }
  /** Clear the Add Page form so the next open shows an empty title field and no validation state. */
  resetAddForm() {
    requestAnimationFrame(() => {
      const form = this.querySelector("#page-manager-add-form");
      form?.clearValidations?.();
      const titleInput = this.querySelector('#page-manager-add-form [name="title"]');
      titleInput?.clear?.();
    });
  }
  handlePageSettingsSelect(e) {
    const value = e.detail?.value;
    if (!value || !this.selectedPage) return;
    switch (value) {
      case "edit_page_settings":
        this.editDrawer?.open(this.selectedPage, this.pages);
        break;
      case "delete_page":
        this.openDeletePagePrompt();
        break;
      case "set_home_page":
        this.handleSetHomePage();
        break;
      case "manage_templates":
        this.showManageTemplatesDialog = true;
        break;
      case "copy_to_templates":
        this.showCopyToTemplatesDialog = true;
        break;
      default:
        break;
    }
  }
  getMicrositeIdFromBoot() {
    const scriptEl = document.getElementById("eab-microsite-boot-data");
    if (!scriptEl?.textContent) return null;
    try {
      const parsed = JSON.parse(scriptEl.textContent);
      const raw = parsed.microsite_id;
      if (raw === null || raw === void 0) return null;
      const n = Number(raw);
      return Number.isFinite(n) && n > 0 ? n : null;
    } catch {
      return null;
    }
  }
  handleCancelCopyToTemplatesDialog() {
    this.showCopyToTemplatesDialog = false;
  }
  handleCopyToTemplatesSubmit(e) {
    const page = this.selectedPage;
    if (!page) return;
    const micrositeId = this.getMicrositeIdFromBoot();
    if (micrositeId == null) {
      notifyMicrositeUser({
        message: "Microsite context is missing. Reload the page and try again.",
        status: "error"
      });
      return;
    }
    const formValue = e.detail?.value || {};
    const name = String(formValue.template_name ?? "").trim();
    if (!name) return;
    void this.submitCopyToTemplates(micrositeId, page.id, name, "");
  }
  handleCopyToTemplatesSaveClick() {
    const formEl = this.querySelector("#page-manager-copy-to-templates-form");
    formEl?.submit?.();
  }
  /** Same slug rules as client `getPageSlug` / draft keys (keep in sync with microsite_editor local_storage). */
  pageSlugForDraft(pageId) {
    const page = this.pages.find((p) => p.id === pageId);
    if (!page) return null;
    const slug = page.slug;
    if (typeof slug === "string" && slug.length > 0) return slug;
    if (page.title != null) {
      const fromTitle = titleToSlug(page.title);
      if (fromTitle) return fromTitle;
    }
    return String(pageId);
  }
  clonePuckPayload(snap) {
    try {
      return JSON.parse(JSON.stringify(snap));
    } catch {
      try {
        const sc = globalThis.structuredClone;
        if (typeof sc === "function") {
          return sc(snap);
        }
      } catch {
      }
    }
    return null;
  }
  getPuckDraftFromLocalStorage(micrositeId, pageId) {
    try {
      const slug = this.pageSlugForDraft(pageId);
      if (!slug) return null;
      const raw = localStorage.getItem(`eab_microsite_draft_${micrositeId}`);
      if (!raw) return null;
      const drafts = JSON.parse(raw);
      if (!drafts || typeof drafts !== "object" || Array.isArray(drafts)) return null;
      const data = drafts[slug];
      if (data == null || typeof data !== "object" || Array.isArray(data)) return null;
      return this.clonePuckPayload(data);
    } catch {
      return null;
    }
  }
  getPuckSnapshotFromEditor() {
    try {
      const snap = window.__eabMicrositeEditorSnapshot;
      if (snap != null && typeof snap === "object" && !Array.isArray(snap)) {
        const cloned = this.clonePuckPayload(snap);
        if (cloned != null) return cloned;
      }
    } catch {
    }
    return null;
  }
  async submitCopyToTemplates(micrositeId, pageId, name, description) {
    const csrf = document.querySelector('meta[name="csrf-token"]')?.content || "";
    try {
      this.copyToTemplatesSubmitting = true;
      let puckData = this.getPuckSnapshotFromEditor();
      if (puckData == null) {
        puckData = this.getPuckDraftFromLocalStorage(micrositeId, pageId);
      }
      if (puckData == null) {
        notifyMicrositeUser({
          message: "The page builder is not active or layout could not be read. Open the editor from Preview (Edit), wait for it to load, then try again.",
          status: "error"
        });
        return;
      }
      const body = {
        microsite_id: micrositeId,
        microsite_page_id: pageId,
        name,
        content: puckData
      };
      if (description.length > 0) body.description = description;
      const res = await fetch("/microsites/page_templates", {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          "X-CSRF-Token": csrf
        },
        credentials: "same-origin",
        body: JSON.stringify({ page_template: body })
      });
      const data = await res.json();
      if (!res.ok) {
        notifyMicrositeUser({
          message: data.error || data.errors?.[0] || `Request failed (${res.status})`,
          status: "error"
        });
        return;
      }
      if (data.success === false) {
        notifyMicrositeUser({
          message: data.error || data.errors?.[0] || "Could not save template.",
          status: "error"
        });
        return;
      }
      this.showCopyToTemplatesDialog = false;
      notifyMicrositeUser({
        message: data.message || "Template saved.",
        status: "success"
      });
    } catch (err) {
      notifyMicrositeUser({
        message: err instanceof Error ? err.message : "Failed to create template",
        status: "error"
      });
    } finally {
      this.copyToTemplatesSubmitting = false;
    }
  }
  handleCreatePage(e) {
    const formValue = e.detail?.value || {};
    const title = String(formValue.title ?? "").trim();
    if (!title) return;
    if (isDuplicatePageTitle(this.pages, title)) {
      const form = this.querySelector("#page-manager-add-form");
      form?.validateCustomValidity?.({ title: [DUPLICATE_PAGE_TITLE_MESSAGE] });
      return;
    }
    const now = (/* @__PURE__ */ new Date()).toISOString();
    const tempId = -Date.now();
    const newPage = {
      id: tempId,
      title,
      name: null,
      slug: titleToSlug(title) || null,
      is_home_page: this.pages.length === 0,
      order: this.pages.length,
      created_at: now,
      updated_at: now
    };
    const updatedPages = [...this.pages, newPage];
    this.showAddDialog = false;
    this.emitPagesUpdate(updatedPages);
    this.emitPageSelect(newPage.id, newPage.title);
  }
  /** Opens hi-prompt for delete confirmation; on confirm runs handleDeletePageConfirm with the page captured now to avoid race. */
  openDeletePagePrompt() {
    const pageToDelete = this.selectedPage;
    if (!pageToDelete || this.pages.length <= 1) return;
    void this.confirmDeletePage(pageToDelete);
  }
  async confirmDeletePage(pageToDelete) {
    const usedInCampaign = await this.pageUsedInUnexpiredCampaign(pageToDelete);
    this.showDeletePagePrompt(
      usedInCampaign ? DELETE_PAGE_IN_CAMPAIGN_DETAILS : DELETE_PAGE_DETAILS,
      pageToDelete
    );
  }
  async pageUsedInUnexpiredCampaign(page) {
    const micrositeId = this.getMicrositeIdFromBoot();
    if (page.id <= 0 || micrositeId == null) return false;
    try {
      const csrf = document.querySelector('meta[name="csrf-token"]')?.content || "";
      const response = await fetch(
        `/microsites/sites/${micrositeId}/active_campaign_usage?page_id=${page.id}`,
        {
          method: "GET",
          headers: { "X-CSRF-Token": csrf, Accept: "application/json" },
          credentials: "same-origin"
        }
      );
      const data = await response.json();
      return response.ok && Boolean(data.used);
    } catch {
      return false;
    }
  }
  showDeletePagePrompt(details, pageToDelete) {
    const win = window;
    const $ip = win.$ip;
    const hipGlobal = win.HIP;
    if (!$ip?.require || !hipGlobal?.fn?.prompt) return;
    $ip.require("@eab-eip/element/hi-prompt/hi-prompt").then(() => {
      hipGlobal.fn.prompt({
        status: "warning",
        title: "Delete Page",
        details,
        controls: ["cancel", "yes"]
      }).then((result) => {
        const hasValueProp = result != null && typeof result === "object" && "value" in result;
        const value = hasValueProp ? result.value : result;
        if (value === 1 || value === "yes") this.handleDeletePageConfirm(pageToDelete);
      }).catch(() => {
      });
    });
  }
  handleDeletePageConfirm(pageToDelete) {
    if (this.pages.length <= 1) return;
    if (!pageToDelete) return;
    const deletedId = pageToDelete.id;
    if (deletedId > 0) {
      this.dispatchEvent(
        new CustomEvent("microsite:page-delete", {
          bubbles: true,
          composed: true,
          detail: { pageId: deletedId }
        })
      );
      return;
    }
    let updatedPages = this.pages.filter((p) => p.id !== deletedId);
    if (updatedPages.length === 1) {
      updatedPages = updatedPages.map((p) => ({ ...p, is_home_page: true }));
    }
    this.emitPagesUpdate(updatedPages);
    if (this.currentPageId === deletedId && updatedPages.length > 0) {
      this.emitPageSelect(updatedPages[0].id);
    }
  }
  handleSetHomePage() {
    if (!this.selectedPage) return;
    const updatedPages = this.pages.map((p) => ({ ...p, is_home_page: p.id === this.selectedPage.id }));
    this.emitPagesUpdate(updatedPages);
  }
  handleCancelAddDialog() {
    this.showAddDialog = false;
  }
  async fetchTemplatePuckData(templateId) {
    const res = await fetch(`/microsites/page_templates/${templateId}`, {
      headers: { Accept: "application/json" },
      credentials: "same-origin"
    });
    if (!res.ok) return null;
    const data = await res.json();
    const puck = data.page_template?.puck_data;
    if (puck == null || typeof puck !== "object" || Array.isArray(puck)) return null;
    return puck;
  }
  async openManageTemplatesDialog() {
    this.manageTemplatesLoading = true;
    try {
      const res = await fetch("/microsites/page_templates", {
        headers: { Accept: "application/json" },
        credentials: "same-origin"
      });
      const data = await res.json();
      const list = Array.isArray(data.page_templates) ? data.page_templates : [];
      this.manageTemplatesList = list.map((t) => ({ id: t.id, name: t.name }));
    } catch {
      this.manageTemplatesList = [];
      notifyMicrositeUser({ message: "Could not load page templates.", status: "error" });
    } finally {
      this.manageTemplatesLoading = false;
    }
    await this.refreshManageTemplatePreview();
  }
  async refreshManageTemplatePreview() {
    this.managePreviewLoading = true;
    try {
      if (this.manageSelectedTemplateKey === "__current__") {
        const page = this.selectedPage;
        const micrositeId = this.getMicrositeIdFromBoot();
        let puck = null;
        if (page != null && micrositeId != null) {
          puck = this.getPuckSnapshotFromEditor() ?? this.getPuckDraftFromLocalStorage(micrositeId, page.id);
        }
        this.managePreviewPuck = puck;
        return;
      }
      const id = parseInt(this.manageSelectedTemplateKey, 10);
      if (Number.isNaN(id)) {
        this.managePreviewPuck = null;
        return;
      }
      this.managePreviewPuck = await this.fetchTemplatePuckData(id);
    } finally {
      this.managePreviewLoading = false;
    }
  }
  /** hi-combobox fires `commit-ended` with `detail.value.value` (see CRM hi-combobox usage). */
  handleManageTemplateComboboxCommit(e) {
    const detail = e.detail?.value;
    let raw;
    if (detail != null && typeof detail === "object" && "value" in detail) {
      raw = detail.value;
    } else if (typeof detail === "string") {
      raw = detail;
    } else {
      const target = e.target;
      raw = target?.value;
    }
    const next = raw != null && String(raw).length > 0 ? String(raw) : "__current__";
    this.manageSelectedTemplateKey = next;
    void this.refreshManageTemplatePreview();
    this.scheduleHideManageTemplateComboboxClear();
  }
  get manageTemplateComboboxInput() {
    return [
      { value: "__current__", title: "Current page" },
      ...this.manageTemplatesList.map((t) => ({ value: String(t.id), title: t.name }))
    ];
  }
  handleCancelManageTemplatesDialog() {
    this.showManageTemplatesDialog = false;
  }
  openHipPrompt(config) {
    return new Promise((resolve) => {
      const win = window;
      const $ip = win.$ip;
      const hipGlobal = win.HIP;
      if (!$ip?.require || !hipGlobal?.fn?.prompt) {
        resolve(window.confirm(config.details));
        return;
      }
      $ip.require("@eab-eip/element/hi-prompt/hi-prompt").then(() => {
        hipGlobal.fn.prompt({
          status: config.status ?? "warning",
          title: config.title,
          details: config.details,
          controls: ["cancel", "yes"]
        }).then((result) => {
          const hasValueProp = result != null && typeof result === "object" && "value" in result;
          const value = hasValueProp ? result.value : result;
          resolve(value === 1 || value === "yes");
        }).catch(() => resolve(false));
      });
    });
  }
  async handleManageDeleteTemplateClick() {
    if (this.manageSelectedTemplateKey === "__current__" || this.manageDeleteSubmitting) return;
    const id = parseInt(this.manageSelectedTemplateKey, 10);
    if (Number.isNaN(id)) return;
    const tpl = this.manageTemplatesList.find((t) => t.id === id);
    const name = tpl?.name ?? "this";
    const ok = await this.openHipPrompt({
      title: `Delete template ${name}?`,
      details: `This will permanently remove the template from your template library.`
    });
    if (!ok) return;
    const csrf = document.querySelector('meta[name="csrf-token"]')?.content || "";
    this.manageDeleteSubmitting = true;
    try {
      const res = await fetch(`/microsites/page_templates/${id}`, {
        method: "DELETE",
        headers: { Accept: "application/json", "X-CSRF-Token": csrf },
        credentials: "same-origin"
      });
      const data = await res.json();
      if (!res.ok || data.success === false) {
        notifyMicrositeUser({
          message: data.error || `Delete failed (${res.status})`,
          status: "error"
        });
        return;
      }
      this.manageTemplatesList = this.manageTemplatesList.filter((t) => t.id !== id);
      this.manageSelectedTemplateKey = "__current__";
      await this.refreshManageTemplatePreview();
      notifyMicrositeUser({ message: "Template deleted.", status: "success" });
    } catch (err) {
      notifyMicrositeUser({
        message: err instanceof Error ? err.message : "Delete failed",
        status: "error"
      });
    } finally {
      this.manageDeleteSubmitting = false;
    }
  }
  async handleManageLoadTemplateClick() {
    if (this.manageSelectedTemplateKey === "__current__" || this.manageLoadSubmitting) return;
    const page = this.selectedPage;
    if (page == null || this.currentPageId == null) return;
    const id = parseInt(this.manageSelectedTemplateKey, 10);
    if (Number.isNaN(id)) return;
    const tpl = this.manageTemplatesList.find((t) => t.id === id);
    const name = tpl?.name ?? "selected";
    const puck = this.managePreviewPuck;
    if (puck == null || Object.keys(puck).length === 0) {
      notifyMicrositeUser({ message: "Nothing to load. Select a template with a valid preview.", status: "error" });
      return;
    }
    const ok = await this.openHipPrompt({
      title: "Load template",
      details: `Are you sure you want to replace this page with the "${name}" template? Your current page layout in the editor will be replaced (you can undo via draft discard if available).`
    });
    if (!ok) return;
    this.manageLoadSubmitting = true;
    try {
      const cloned = this.clonePuckPayload(puck);
      if (cloned == null) {
        notifyMicrositeUser({ message: "Could not read template data.", status: "error" });
        return;
      }
      this.showManageTemplatesDialog = false;
      this.emitPageSelect(this.currentPageId, void 0, cloned);
      notifyMicrositeUser({ message: `Template "${name}" loaded into the editor.`, status: "success" });
    } finally {
      this.manageLoadSubmitting = false;
    }
  }
  /** Create: trigger form submit. submit-ended is bound on the form to avoid accumulated listeners when validation fails. */
  handleAddCreateClick() {
    const formEl = this.querySelector("#page-manager-add-form");
    formEl?.submit?.();
  }
  /** Forward drawer's pages-update so parent (page builder) receives it and UI reflects changes. */
  handleEditDrawerPagesUpdate(e) {
    const pages = e.detail?.pages;
    if (!Array.isArray(pages)) return;
    e.stopPropagation();
    this.emitPagesUpdate(pages);
  }
  renderPageManager() {
    if (this.loading) {
      return html`
        <div class="eab-page-manager__loading">
          <hi-indicator></hi-indicator>
        </div>
      `;
    }
    const selectedPage = this.selectedPage;
    return html`
      <div class="eab-page-manager">
        <div class="eab-page-manager__toolbar">
          ${this.showHtmlPageManagerChrome() ? html`
                ${this.historyMode ? nothing : html`
                <div class="eab-page-manager__dropdown-wrap">
                  ${keyed(
      `${this.currentPageId}-${selectedPage?.title}-${selectedPage?.is_home_page}`,
      html`
                      <hi-input-group>
                        <hi-label for="page-manager-page-select">Page</hi-label>
                        <hi-dropdown id="page-manager-page-select" kind="value" placeholder="Select page" cap-height>
                          <hi-select
                            aesthetic="dropdown"
                            mode="value"
                            .value=${this.currentPageId != null ? String(this.currentPageId) : ""}
                            @selected-ended=${this.handleDropdownChange}
                          >
                            ${this.pages.map(
        (page) => html`
                                <hi-option value=${String(page.id)}>
                                  ${page.title}${page.is_home_page && !this.isHtmlEditorMode() ? " (Home)" : ""}
                                </hi-option>
                              `
      )}
                          </hi-select>
                        </hi-dropdown>
                      </hi-input-group>
                    `
    )}
                </div>
                  `}
                ${this.previewMode && this.hasPersonalizationOptions ? html`
                  <span class="eab-page-manager__action">
                    <hi-button
                      aesthetic="secondary"
                      @trigger-ended=${() => {
      const drawer = document.getElementById("microsite-personalization-preview-drawer");
      if (drawer) drawer.opened = true;
    }}
                    >
                      <hi-icon kind="gear"></hi-icon>
                      <span>Personalization Preview Settings</span>
                    </hi-button>
                  </span>
                ` : nothing}
              ` : nothing}
          ${!this.previewMode ? html`
                ${this.isHtmlEditorMode() ? html`
                      <span id="page-manager-import-html-btn" class="eab-page-manager__action">
                        <hi-button
                          aesthetic="secondary"
                          size="medium"
                          title="Add HTML"
                          @trigger-ended=${this.handleImportHtmlClick}
                        >
                          <hi-icon kind="plus" mode="presentation"></hi-icon>
                          <span>Add HTML</span>
                        </hi-button>
                      </span>
                    ` : html`
                      <span id="page-manager-add-page-btn" class="eab-page-manager__action">
                        <hi-button aesthetic="secondary" size="medium" title="Add Page" @trigger-ended=${this.handleAddPageClick}>
                          <hi-icon kind="plus" mode="presentation"></hi-icon>
                          <span>Add Page</span>
                        </hi-button>
                      </span>
                    `}
                ${this.showHtmlPageManagerChrome() ? html`
                      <span
                        id="page-manager-settings-wrap"
                        class="eab-page-manager__action ${selectedPage ? "" : "eab-page-manager__action--hidden"}"
                      >
                        <hi-button
                          id="page-settings-btn"
                          aesthetic="secondary"
                          size="medium"
                          title="Page Settings"
                          aria-haspopup="true"
                          aria-expanded="false"
                        >
                          <hi-icon kind="settings" mode="presentation"></hi-icon>
                          <span>Page Settings</span>
                        </hi-button>
                        <hi-menu
                          id="page-settings-menu"
                          for="page-settings-btn"
                          placement="bottom-start"
                          @selected-ended=${this.handlePageSettingsSelect}
                        >
                          <hi-pick value="edit_page_settings" heading="Edit Page"></hi-pick>
                          ${this.canManageTemplates && !this.isHtmlEditorMode() ? html`<hi-pick value="manage_templates" heading="Manage Templates"></hi-pick>` : nothing}
                          ${this.canManageTemplates && !this.isHtmlEditorMode() ? html`<hi-pick value="copy_to_templates" heading="Save as Template"></hi-pick>` : nothing}
                          ${selectedPage && this.pages.length > 1 ? html`<hi-pick value="delete_page" heading="Delete Page"></hi-pick>` : nothing}
                          ${selectedPage && !selectedPage.is_home_page && !this.isHtmlEditorMode() ? html`
                                <hi-pick value="set_home_page" heading="Make Current Page The Homepage"></hi-pick>
                              ` : nothing}
                        </hi-menu>
                        <hi-binding source="page-settings-btn" sink="page-settings-menu" field="activate"></hi-binding>
                      </span>
                    ` : nothing}
                ${this.siteVersioningEnabled ? html`
                      <span id="page-manager-history-wrap" class="eab-page-manager__action">
                        <hi-button
                          id="microsite-view-history-btn"
                          aesthetic="secondary"
                          size="medium"
                          title="View History"
                          @trigger-ended=${this.openVersionHistory}
                        >
                          <hi-icon kind="history" mode="presentation"></hi-icon>
                          <span>View History</span>
                        </hi-button>
                      </span>
                    ` : nothing}
                ${this.canShowHtmlAiChrome ? html`
                      <span id="page-manager-enhance-ai-btn" class="eab-page-manager__action">
                        <hi-button
                          aesthetic="ghost"
                          bordered
                          size="medium"
                          title="Enhance with AI"
                          @trigger-ended=${this.handleEnhanceAiClick}
                        >
                          <hi-icon kind="sparkles" mode="presentation"></hi-icon>
                          <span>Enhance with AI</span>
                        </hi-button>
                      </span>
                    ` : nothing}
              ` : nothing}
        </div>
      </div>
    `;
  }
  renderCopyToTemplatesDialog() {
    const page = this.selectedPage;
    if (!this.showCopyToTemplatesDialog || page == null) return nothing;
    return keyed(
      page.id,
      html`
        <hi-dialog ?opened=${true} tight-screen @close-ended=${this.handleCancelCopyToTemplatesDialog}>
          <h3 slot="heading">Save as Template</h3>

          <hi-form
            id="page-manager-copy-to-templates-form"
            disable-http
            @submit-ended=${this.handleCopyToTemplatesSubmit}
          >
            <hi-input-group>
              <hi-label>Template name</hi-label>
              <hi-input
                type="text"
                name="template_name"
                form="page-manager-copy-to-templates-form"
                .value=${page.title}
                placeholder="Name shown in the template list"
                required
              ></hi-input>
            </hi-input-group>
          </hi-form>
          <hi-button type="button" slot="controls" aesthetic="ghost" @trigger-ended=${this.handleCancelCopyToTemplatesDialog}>
            <span>Cancel</span>
          </hi-button>
          <hi-button
            type="button"
            slot="controls"
            aesthetic="primary"
            ?disabled=${this.copyToTemplatesSubmitting}
            @trigger-ended=${this.handleCopyToTemplatesSaveClick}
          >
            <span>Save template</span>
          </hi-button>
        </hi-dialog>
      `
    );
  }
  renderAddDialog() {
    return html`
      <hi-dialog
        ?opened=${this.showAddDialog}
        @close-ended=${this.handleCancelAddDialog}
        tight-screen
        disallow-close
      >
        <h3 slot="heading">Add New Page</h3>
        <hi-form
          id="page-manager-add-form"
          disable-http
          @submit-ended=${this.handleCreatePage}
        >
          <hi-input-group>
            <hi-label>Page Name</hi-label>
            <hi-input
              type="text"
              name="title"
              placeholder="Enter page name"
              required
            ></hi-input>
          </hi-input-group>
        </hi-form>
        <hi-button type="button" slot="controls" aesthetic="ghost" @trigger-ended=${this.handleCancelAddDialog}>
          <span>Cancel</span>
        </hi-button>
        <hi-button type="button" slot="controls" aesthetic="primary" @trigger-ended=${this.handleAddCreateClick}>
          <span>Create</span>
        </hi-button>
      </hi-dialog>
    `;
  }
  renderManageTemplatesDialog() {
    const page = this.selectedPage;
    if (!this.showManageTemplatesDialog || page == null) return nothing;
    const selectedIsCurrent = this.manageSelectedTemplateKey === "__current__";
    return keyed(
      page.id,
      html`
        <hi-dialog
          class="eab-manage-templates-dialog"
          ?opened=${true}
          wide-screen
          @close-ended=${this.handleCancelManageTemplatesDialog}
        >
          <h3 slot="heading">Manage Templates</h3>
          <p class="eab-manage-templates__page-line">Page: <strong>${page.title}</strong></p>
          <div class="eab-manage-templates__row">
            <hi-input-group class="eab-manage-templates__select-wrap">
              <hi-label for="microsite-page-manager-manage-template-select">Select template</hi-label>
              ${this.manageTemplatesLoading ? html`<div class="eab-page-manager__add-template-loading"><hi-indicator></hi-indicator></div>` : html`
                    <hi-combobox
                      id="microsite-page-manager-manage-template-select"
                      aria-label="Select template"
                      placeholder="Search or select a template"
                      .value=${this.manageSelectedTemplateKey}
                      .input=${this.manageTemplateComboboxInput}
                      @commit-ended=${this.handleManageTemplateComboboxCommit}
                    ></hi-combobox>
                  `}
            </hi-input-group>
            <hi-button
              type="button"
              aesthetic="ghost"
              size="medium"
              ?disabled=${selectedIsCurrent || this.manageDeleteSubmitting}
              @trigger-ended=${() => void this.handleManageDeleteTemplateClick()}
            >
              <span>Delete template</span>
            </hi-button>
          </div>
          <div class="eab-manage-templates__preview">
            <div
              ${ref(this.manageTemplatePreviewMountRef)}
              id="eab-manage-template-preview-mount"
              class="eab-manage-templates__preview-mount"
            ></div>
            ${this.managePreviewLoading ? html`<div class="eab-manage-templates__preview-loading"><hi-indicator></hi-indicator></div>` : this.managePreviewPuck == null ? html`<div class="eab-manage-templates__preview-empty">
                    No preview available. Open the editor or select a template.
                  </div>` : nothing}
          </div>
          <hi-button type="button" slot="controls" aesthetic="ghost" @trigger-ended=${this.handleCancelManageTemplatesDialog}>
            <span>Cancel</span>
          </hi-button>
          <hi-button
            type="button"
            slot="controls"
            aesthetic="primary"
            ?disabled=${selectedIsCurrent || this.manageLoadSubmitting || this.managePreviewLoading}
            @trigger-ended=${() => void this.handleManageLoadTemplateClick()}
          >
            <span>Load template</span>
          </hi-button>
        </hi-dialog>
      `
    );
  }
  render() {
    return html`
      ${this.renderPageManager()}
      ${this.renderAddDialog()}
      ${this.renderCopyToTemplatesDialog()}
      ${this.renderManageTemplatesDialog()}
      <stc-microsite-edit-page-drawer
        editor-mode=${this.editorMode}
        @microsite:pages-update=${this.handleEditDrawerPagesUpdate}
      ></stc-microsite-edit-page-drawer>
    `;
  }
}
__decorateClass([
  property({ type: Array })
], MicrositePageManager.prototype, "pages", 2);
__decorateClass([
  property({ type: String, attribute: "editor-mode", reflect: true })
], MicrositePageManager.prototype, "editorMode", 2);
__decorateClass([
  property({ type: Boolean, attribute: "html-editor-has-content" })
], MicrositePageManager.prototype, "htmlEditorHasContent", 2);
__decorateClass([
  property({ type: Number, attribute: "current-page-id" })
], MicrositePageManager.prototype, "currentPageId", 2);
__decorateClass([
  property({ type: Boolean, attribute: "preview-mode", reflect: true })
], MicrositePageManager.prototype, "previewMode", 2);
__decorateClass([
  property({ type: Boolean, attribute: "history-mode", reflect: true })
], MicrositePageManager.prototype, "historyMode", 2);
__decorateClass([
  property({ type: Boolean })
], MicrositePageManager.prototype, "hasPersonalizationOptions", 2);
__decorateClass([
  property({ type: Boolean })
], MicrositePageManager.prototype, "loading", 2);
__decorateClass([
  property({ type: Boolean })
], MicrositePageManager.prototype, "canManageTemplates", 2);
__decorateClass([
  property({ type: Boolean })
], MicrositePageManager.prototype, "siteVersioningEnabled", 2);
__decorateClass([
  state()
], MicrositePageManager.prototype, "showAddDialog", 2);
__decorateClass([
  state()
], MicrositePageManager.prototype, "showCopyToTemplatesDialog", 2);
__decorateClass([
  state()
], MicrositePageManager.prototype, "copyToTemplatesSubmitting", 2);
__decorateClass([
  state()
], MicrositePageManager.prototype, "showManageTemplatesDialog", 2);
__decorateClass([
  state()
], MicrositePageManager.prototype, "manageTemplatesList", 2);
__decorateClass([
  state()
], MicrositePageManager.prototype, "manageTemplatesLoading", 2);
__decorateClass([
  state()
], MicrositePageManager.prototype, "manageSelectedTemplateKey", 2);
__decorateClass([
  state()
], MicrositePageManager.prototype, "managePreviewPuck", 2);
__decorateClass([
  state()
], MicrositePageManager.prototype, "managePreviewLoading", 2);
__decorateClass([
  state()
], MicrositePageManager.prototype, "manageDeleteSubmitting", 2);
__decorateClass([
  state()
], MicrositePageManager.prototype, "manageLoadSubmitting", 2);
customElements.define("stc-microsite-page-manager", MicrositePageManager);
